import { useEffect, useState } from 'react'
import { useQueries } from '@tanstack/react-query'

import { fetchProducts } from '../api'
import { ProductBranchLinkMode, ProductType } from '../types'
import type { ProductListQuery, ProductSummary } from '../types'
import { productsKey, useProductActions } from './use-products'

/** An unlink waiting on the merchant's say-so, because it would clear counted stock. */
export interface PendingBranchRemoval {
  products: ProductSummary[]
  branchId: string
  branchName: string
}

const pendingKey = (productId: string, branchId: string) => `${productId}:${branchId}`

/**
 * Putting products on and off branches from the Branches tab — both the grid and
 * the single-branch view write through here.
 *
 * Pending cells are tracked per product *and* branch rather than read off the
 * mutation: a merchant ticks several boxes faster than each lands, and the
 * mutation only remembers the last. `mutateAsync` settles per call and only
 * after the list has been refetched, so a box never springs back to its old
 * state in between.
 *
 * Unlinking a stockable product asks first. The count a branch holds for a
 * product lives on the link itself, so unlinking clears it and adding the
 * product back later starts that branch from zero. A made-to-order product has
 * no count to lose and goes straight through.
 */
export function useBranchLinks() {
  const { linkBranches } = useProductActions()
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set())
  const [error, setError] = useState<unknown>(null)
  const [removal, setRemoval] = useState<PendingBranchRemoval | null>(null)

  function write(mode: ProductBranchLinkMode, products: ProductSummary[], branchId: string) {
    const ids = products.map((product) => product.id)
    if (ids.length === 0) return

    const keys = ids.map((id) => pendingKey(id, branchId))

    setError(null)
    setPendingKeys((current) => new Set([...current, ...keys]))

    linkBranches
      .mutateAsync({ mode, body: { productIds: ids, tenantBranchIds: [branchId] } })
      .catch(setError)
      .finally(() =>
        setPendingKeys((current) => {
          const next = new Set(current)
          keys.forEach((key) => next.delete(key))
          return next
        }),
      )
  }

  function link(products: ProductSummary[], branchId: string) {
    write(ProductBranchLinkMode.Link, products, branchId)
  }

  function unlink(products: ProductSummary[], branchId: string, branchName: string) {
    if (products.length === 0) return

    if (products.some((product) => product.type === ProductType.Stockable)) {
      setRemoval({ products, branchId, branchName })
    } else {
      write(ProductBranchLinkMode.Unlink, products, branchId)
    }
  }

  function confirmRemoval() {
    if (removal) write(ProductBranchLinkMode.Unlink, removal.products, removal.branchId)
    setRemoval(null)
  }

  return {
    link,
    unlink,
    isPending: (productId: string, branchId: string) =>
      pendingKeys.has(pendingKey(productId, branchId)),
    error,
    removal,
    confirmRemoval,
    cancelRemoval: () => setRemoval(null),
  }
}

/**
 * How many products each branch sells, keyed by branch id. `undefined` while a
 * count is on its way.
 *
 * One row per branch from the list endpoint, which already answers with a total
 * — the same key `useProducts` would build for that query, so the cache is
 * shared and every write's invalidation refreshes these too.
 */
export function useBranchProductCounts(branchIds: string[]): Record<string, number | undefined> {
  return useQueries({
    queries: branchIds.map((branchId) => {
      const query: ProductListQuery = { limit: 1, offset: 0, branchId }

      return { queryKey: [...productsKey, 'list', query], queryFn: () => fetchProducts(query) }
    }),
    combine: (results) =>
      Object.fromEntries(results.map((result, index) => [branchIds[index], result.data?.count])),
  })
}

/** `value`, once it has stopped changing for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return settled
}
